import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, RefreshCw, CheckCircle, TrendingUp, Inbox } from 'lucide-react';
import { verifyEmail, resendVerification } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const VerifyEmailPage = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthData } = useAuth();

  // Get email from registration
  const email = location.state?.email || '';

  useEffect(() => {
    if (!email) {
      navigate('/register');
      return;
    }
  }, [email, navigate]);

  // Cooldown timer for resend
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;
    
    const newCode = [...code];
    pastedData.split('').forEach((char, i) => {
      if (i < 6) newCode[i] = char;
    });
    setCode(newCode);
    
    // Focus last filled input or next empty
    const lastIndex = Math.min(pastedData.length - 1, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  const handleVerify = async () => {
    const verificationCode = code.join('');
    if (verificationCode.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await verifyEmail({ email, code: verificationCode });
      
      // Auto-authenticate: Store token and user data
      if (response.data.token && response.data.user) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('role', 'user');
        setAuthData(response.data.token, response.data.user, 'user');
      }
      
      setVerified(true);
      toast.success('Email verified successfully! Welcome to MetaBlockify!');
      
      // Redirect directly to dashboard after brief success animation
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1500);
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed';
      toast.error(message);
      // Clear code on error
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    
    setResending(true);
    try {
      await resendVerification({ email });
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setCooldown(60); // 60 second cooldown
      toast.success('A new verification code has been sent to your email!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to resend code';
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  if (verified) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="animated-bg" />
        <div className="noise-overlay" />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-12 text-center max-w-md"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-white mb-4">
            Welcome to MetaBlockify!
          </h1>
          <p className="text-slate-400 mb-6">
            Your email has been verified. Taking you to your dashboard...
          </p>
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="animated-bg" />
      <div className="noise-overlay" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-8 w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-gray-900" />
            </div>
            <span className="text-2xl font-bold font-['Outfit'] gradient-text">
              MetaBlockify
            </span>
          </Link>
          
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-white mb-2">
            Verify Your Email
          </h1>
          <p className="text-slate-400">
            We've sent a verification code to
          </p>
          <p className="text-cyan-400 font-medium">{email}</p>
        </div>

        {/* Email Sent Notice */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 rounded-xl border border-cyan-500/20"
        >
          <div className="flex items-center gap-3">
            <Inbox className="w-5 h-5 text-cyan-400 flex-shrink-0" />
            <div>
              <p className="text-sm text-white font-medium">Check your inbox</p>
              <p className="text-xs text-slate-400">Enter the 6-digit code from your email</p>
            </div>
          </div>
        </motion.div>

        {/* Code Input */}
        <div className="mb-6">
          <div className="flex gap-2 justify-center" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold bg-slate-900/50 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                data-testid={`code-input-${index}`}
              />
            ))}
          </div>
        </div>

        {/* Verify Button */}
        <button
          onClick={handleVerify}
          disabled={loading || code.join('').length !== 6}
          className="btn-primary w-full flex items-center justify-center gap-2 py-4 mb-4"
          data-testid="verify-btn"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              Verify & Continue
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        {/* Resend */}
        <div className="text-center">
          <p className="text-slate-400 text-sm mb-2">Didn't receive the code?</p>
          <button
            onClick={handleResend}
            disabled={resending || cooldown > 0}
            className={`font-medium flex items-center justify-center gap-2 mx-auto transition-colors ${
              cooldown > 0 ? 'text-slate-500 cursor-not-allowed' : 'text-cyan-400 hover:text-cyan-300'
            }`}
            data-testid="resend-btn"
          >
            {resending ? (
              <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <RefreshCw className={`w-4 h-4 ${cooldown > 0 ? '' : ''}`} />
            )}
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Code'}
          </button>
        </div>

        {/* Spam Notice */}
        <div className="mt-6 p-3 bg-white/5 rounded-lg">
          <p className="text-xs text-slate-500 text-center">
            Can't find the email? Check your spam or junk folder.
          </p>
        </div>

        {/* Back to Login */}
        <div className="mt-6 pt-6 border-t border-white/5 text-center">
          <p className="text-slate-400 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
